import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FolderIcon from '@mui/icons-material/Folder';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useNavigate } from 'react-router-dom';
import useApi from '../../hooks/useApi';
import ManageSharedAccessDialog from './ManageSharedAccessDialog';
import { FONT_FAMILY_PRIMARY } from '../../constants/fonts';
import { useTenant } from '../../contexts/TenantContext';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { useErrorToast } from '../common';

function formatShortDate(v) {
  if (v == null) return '—';
  const d = typeof v === 'string' ? new Date(v) : v;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Normalize API access level for display (View / Edit). */
function formatAccessLabel(v) {
  if (v == null || v === '') return '—';
  const s = String(v).trim().toLowerCase();
  if (s === 'view' || s === 'read' || s === 'readonly') return 'View';
  if (s === 'edit' || s === 'write' || s === 'full') return 'Edit';
  return String(v).charAt(0).toUpperCase() + String(v).slice(1);
}

/** Matches Clients / Shared-with-you “Show users” link styling */
const SHOW_USERS_LINK_SX = {
  cursor: 'pointer',
  textDecoration: 'none',
  color: '#1976d2',
  fontWeight: 500,
  fontFamily: FONT_FAMILY_PRIMARY,
  '&:hover': { textDecoration: 'underline' },
};

const BY_ME_TABLE_SX = {
  width: '100%',
  minWidth: { xs: 560, sm: 720, md: 880 },
  fontSize: { xs: '0.875rem', md: '1rem' },
  tableLayout: 'fixed',
};

const WITH_ME_TABLE_SX = {
  width: '100%',
  minWidth: { xs: 320, sm: 400, md: 480 },
  fontSize: { xs: '0.875rem', md: '1rem' },
  tableLayout: 'fixed',
};

const byMeHeadCellSx = {
  fontFamily: FONT_FAMILY_PRIMARY,
  fontWeight: 600,
  fontSize: { xs: '0.875rem', md: '1rem' },
};

/**
 * Tabs: resources shared by the current user vs shared with them.
 * Default: shared by you (nested folders + dashboards, user list modal, remove all).
 */
export default function SharedAccessHub({ tenantId }) {
  const navigate = useNavigate();
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const { hasPermission } = useTenant();
  const canShareDash = hasPermission('share_dashboard');
  const canShareFolder = hasPermission('share_folder');
  const canViewSharedWithMe = hasPermission('view_shared_dashboards');
  const showByMeTab = canShareDash || canShareFolder;
  const showWithMeTab = canViewSharedWithMe;

  const [tab, setTab] = useState('byMe'); // byMe | withMe
  const [byMeLoading, setByMeLoading] = useState(false);
  const [withMeLoading, setWithMeLoading] = useState(false);
  const [byMeFolders, setByMeFolders] = useState([]);
  const [byMeDashboards, setByMeDashboards] = useState([]);
  const [withMeItems, setWithMeItems] = useState([]);
  const [error, setError] = useState('');
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError('');
    }
  }, [error, showErrorToast]);

  const [usersModal, setUsersModal] = useState({
    open: false,
    resourceType: 'dashboard',
    resourceId: null,
    resourceLabel: '',
  });
  const [confirmRevoke, setConfirmRevoke] = useState({
    open: false,
    resourceType: 'dashboard',
    resourceId: null,
    resourceLabel: '',
  });
  const [revoking, setRevoking] = useState(false);

  const loadByMe = useCallback(async () => {
    if (!tenantId) return;
    setByMeLoading(true);
    setError('');
    try {
      const res = await apiRef.current.get('/sharing', {
        params: { byMe: '1' },
        headers: { 'X-Tenant-ID': tenantId },
      });
      const data = res.data?.data ?? {};
      setByMeFolders(Array.isArray(data.folders) ? data.folders : []);
      setByMeDashboards(Array.isArray(data.dashboards) ? data.dashboards : []);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not load shares you created.'));
      setByMeFolders([]);
      setByMeDashboards([]);
    } finally {
      setByMeLoading(false);
    }
  }, [tenantId]);

  const loadWithMe = useCallback(async () => {
    if (!tenantId) return;
    setWithMeLoading(true);
    setError('');
    try {
      const res = await apiRef.current.get('/sharing', {
        params: { withMe: '1' },
        headers: { 'X-Tenant-ID': tenantId },
      });
      const items = res.data?.data?.items ?? [];
      setWithMeItems(Array.isArray(items) ? items : []);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not load items shared with you.'));
      setWithMeItems([]);
    } finally {
      setWithMeLoading(false);
    }
  }, [tenantId]);

  const refreshSharingLists = useCallback(() => {
    if (showByMeTab) loadByMe();
    if (showWithMeTab) loadWithMe();
  }, [loadByMe, loadWithMe, showByMeTab, showWithMeTab]);

  const effectiveTab = useMemo(() => {
    if (!showByMeTab && showWithMeTab) return 'withMe';
    if (showByMeTab && !showWithMeTab) return 'byMe';
    return tab;
  }, [showByMeTab, showWithMeTab, tab]);

  useEffect(() => {
    if (effectiveTab === 'byMe' && showByMeTab) loadByMe();
    else if (effectiveTab === 'withMe' && showWithMeTab) loadWithMe();
  }, [effectiveTab, loadByMe, loadWithMe, showByMeTab, showWithMeTab]);

  const folderIdSet = useMemo(() => new Set(byMeFolders.map((f) => f.folderId).filter(Boolean)), [byMeFolders]);

  const childFolders = useCallback(
    (parentId) => {
      if (parentId == null) {
        return byMeFolders.filter(
          (f) => !f.parentFolderId || !folderIdSet.has(f.parentFolderId)
        );
      }
      return byMeFolders.filter((f) => (f.parentFolderId || null) === parentId);
    },
    [byMeFolders, folderIdSet]
  );

  const dashboardsInFolder = useCallback(
    (folderId) => byMeDashboards.filter((d) => d.folderId === folderId),
    [byMeDashboards]
  );

  const rootDashboardsOnly = useMemo(
    () =>
      byMeDashboards.filter(
        (d) => !d.folderId || !folderIdSet.has(d.folderId)
      ),
    [byMeDashboards, folderIdSet]
  );

  const openUsers = (resourceType, resourceId, resourceLabel) => {
    if (!resourceId) return;
    setUsersModal({ open: true, resourceType, resourceId, resourceLabel });
  };

  const openRevokeAll = (resourceType, resourceId, resourceLabel) => {
    if (!resourceId) return;
    setConfirmRevoke({ open: true, resourceType, resourceId, resourceLabel });
  };

  const runRevokeAll = async () => {
    const { resourceType, resourceId } = confirmRevoke;
    if (!resourceId || !tenantId) return;
    setRevoking(true);
    setError('');
    try {
      const params =
        resourceType === 'folder'
          ? { revokeAll: '1', folderId: resourceId }
          : { revokeAll: '1', dashboardId: resourceId };
      await apiRef.current.delete('/sharing', {
        params,
        headers: { 'X-Tenant-ID': tenantId },
      });
      setConfirmRevoke((s) => ({ ...s, open: false }));
      await loadByMe();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Remove all failed.'));
    } finally {
      setRevoking(false);
    }
  };

  const renderFolderRows = (parentId, depth) => {
    const kids = childFolders(parentId);
    kids.sort(
      (a, b) =>
        (a.displayOrder ?? 0) - (b.displayOrder ?? 0) ||
        (a.folderName || '').localeCompare(b.folderName || '', undefined, { sensitivity: 'base' })
    );
    const rows = [];
    for (const f of kids) {
      const dashHere = dashboardsInFolder(f.folderId);
      rows.push(
        <TableRow key={`f-${f.folderId}`} hover sx={{ '& > *': { borderBottom: 'inherit' } }}>
          <TableCell
            sx={{
              fontFamily: FONT_FAMILY_PRIMARY,
              pl: 1 + depth * 2,
              width: '45%',
              maxWidth: 0,
              verticalAlign: 'middle',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <FolderIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
              <Typography variant="body2" component="span" noWrap sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
                {f.folderName || 'Folder'}
              </Typography>
            </Box>
          </TableCell>
          <TableCell
            sx={{
              fontFamily: FONT_FAMILY_PRIMARY,
              width: '13%',
              whiteSpace: 'nowrap',
              verticalAlign: 'middle',
            }}
          >
            {formatShortDate(f.firstSharedAt)}
          </TableCell>
          <TableCell align="center" sx={{ fontFamily: FONT_FAMILY_PRIMARY, width: '22%', verticalAlign: 'middle' }}>
            {canShareFolder && (f.activeShareCount ?? 0) > 0 ? (
              <Link
                component="button"
                variant="body2"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openUsers('folder', f.folderId, f.folderName);
                }}
                sx={SHOW_USERS_LINK_SX}
              >
                Show users ({f.activeShareCount ?? 0})
              </Link>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
                —
              </Typography>
            )}
          </TableCell>
          <TableCell align="right" sx={{ fontFamily: FONT_FAMILY_PRIMARY, width: '20%', verticalAlign: 'middle' }}>
            {canShareFolder ? (
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<DeleteOutlineIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  openRevokeAll('folder', f.folderId, f.folderName);
                }}
                sx={{ fontFamily: FONT_FAMILY_PRIMARY }}
              >
                Remove all
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
                —
              </Typography>
            )}
          </TableCell>
        </TableRow>
      );
      rows.push(...renderFolderRows(f.folderId, depth + 1));
      for (const d of dashHere) {
        rows.push(
          <TableRow key={`d-${d.dashboardId}-in-${f.folderId}`} hover>
            <TableCell
              sx={{
                fontFamily: FONT_FAMILY_PRIMARY,
                pl: 3 + depth * 2,
                width: '45%',
                maxWidth: 0,
                verticalAlign: 'middle',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                <DashboardIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                <Typography variant="body2" component="span" noWrap sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
                  {d.dashboardName || 'Dashboard'}
                </Typography>
              </Box>
            </TableCell>
            <TableCell
              sx={{
                fontFamily: FONT_FAMILY_PRIMARY,
                width: '13%',
                whiteSpace: 'nowrap',
                verticalAlign: 'middle',
              }}
            >
              {formatShortDate(d.firstSharedAt)}
            </TableCell>
            <TableCell align="center" sx={{ fontFamily: FONT_FAMILY_PRIMARY, width: '22%', verticalAlign: 'middle' }}>
              {canShareDash && (d.activeShareCount ?? 0) > 0 ? (
                <Link
                  component="button"
                  variant="body2"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openUsers('dashboard', d.dashboardId, d.dashboardName);
                  }}
                  sx={SHOW_USERS_LINK_SX}
                >
                  Show users ({d.activeShareCount ?? 0})
                </Link>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
                  —
                </Typography>
              )}
            </TableCell>
            <TableCell align="right" sx={{ fontFamily: FONT_FAMILY_PRIMARY, width: '20%', verticalAlign: 'middle' }}>
              {canShareDash ? (
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    openRevokeAll('dashboard', d.dashboardId, d.dashboardName);
                  }}
                  sx={{ fontFamily: FONT_FAMILY_PRIMARY }}
                >
                  Remove all
                </Button>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
                  —
                </Typography>
              )}
            </TableCell>
          </TableRow>
        );
      }
    }
    return rows;
  };

  const rootDashboardRows = useMemo(
    () =>
      rootDashboardsOnly.map((d) => (
        <TableRow key={`root-d-${d.dashboardId}`} hover>
          <TableCell
            sx={{
              fontFamily: FONT_FAMILY_PRIMARY,
              width: '45%',
              maxWidth: 0,
              verticalAlign: 'middle',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
              <DashboardIcon fontSize="small" color="action" sx={{ flexShrink: 0, mt: 0.25 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" noWrap sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
                  {d.dashboardName || 'Dashboard'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
                  (not in a folder)
                </Typography>
              </Box>
            </Box>
          </TableCell>
          <TableCell
            sx={{
              fontFamily: FONT_FAMILY_PRIMARY,
              width: '13%',
              whiteSpace: 'nowrap',
              verticalAlign: 'middle',
            }}
          >
            {formatShortDate(d.firstSharedAt)}
          </TableCell>
          <TableCell align="center" sx={{ fontFamily: FONT_FAMILY_PRIMARY, width: '22%', verticalAlign: 'middle' }}>
            {canShareDash && (d.activeShareCount ?? 0) > 0 ? (
              <Link
                component="button"
                variant="body2"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openUsers('dashboard', d.dashboardId, d.dashboardName);
                }}
                sx={SHOW_USERS_LINK_SX}
              >
                Show users ({d.activeShareCount ?? 0})
              </Link>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
                —
              </Typography>
            )}
          </TableCell>
          <TableCell align="right" sx={{ fontFamily: FONT_FAMILY_PRIMARY, width: '20%', verticalAlign: 'middle' }}>
            {canShareDash ? (
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<DeleteOutlineIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  openRevokeAll('dashboard', d.dashboardId, d.dashboardName);
                }}
                sx={{ fontFamily: FONT_FAMILY_PRIMARY }}
              >
                Remove all
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
                —
              </Typography>
            )}
          </TableCell>
        </TableRow>
      )),
    [rootDashboardsOnly, canShareDash]
  );

  const handleWithMeRowClick = (row) => {
    const kind = row.kind || (row.dashboardId ? 'dashboard' : 'folder');
    if (kind === 'folder' && row.folderId) {
      navigate(`/dashboards?folder=${encodeURIComponent(row.folderId)}`);
      return;
    }
    if (row.dashboardId) {
      navigate(`/dashboard/${row.projectId || row.dashboardId}`);
    }
  };

  const withMeRowSx = {
    cursor: 'pointer',
    '&:focus-visible': { outline: '2px solid', outlineOffset: -2, outlineColor: 'primary.main' },
  };

  if (!showByMeTab && !showWithMeTab) {
    return (
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" component="h1" sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
            Shared Access
          </Typography>
        </Box>
        <Alert severity="info" sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
          You do not have permission to view shared access for this tenant.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" component="h1" sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
          Shared Access
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontFamily: FONT_FAMILY_PRIMARY, mt: 0.5, maxWidth: 720 }}
        >
          {showByMeTab && showWithMeTab
            ? 'See what you have shared with others and what others have shared with you.'
            : showByMeTab
              ? 'Folders and dashboards you have shared with others.'
              : 'Folders and dashboards others have shared with you.'}
        </Typography>
      </Box>

      {showByMeTab && showWithMeTab ? (
        <ToggleButtonGroup
          value={tab}
          exclusive
          onChange={(_, v) => v != null && setTab(v)}
          size="small"
          sx={{ mb: 2, flexWrap: 'wrap' }}
          aria-label="Shared access view"
        >
          <ToggleButton value="byMe" sx={{ fontFamily: FONT_FAMILY_PRIMARY, textTransform: 'none' }}>
            Shared by you
          </ToggleButton>
          <ToggleButton value="withMe" sx={{ fontFamily: FONT_FAMILY_PRIMARY, textTransform: 'none' }}>
            Shared with you
          </ToggleButton>
        </ToggleButtonGroup>
      ) : null}

      {effectiveTab === 'byMe' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: FONT_FAMILY_PRIMARY, mb: 2 }}>
            Use the <strong>Users</strong> column to open the share list, or <strong>Actions</strong> to remove
            everyone at once.
          </Typography>
          {byMeLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              elevation={1}
              sx={{
                borderRadius: '8px',
                overflow: 'auto',
                maxWidth: '100%',
              }}
            >
              <Table size="small" sx={BY_ME_TABLE_SX}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ ...byMeHeadCellSx, width: '45%', maxWidth: 0 }}>Folder / dashboard</TableCell>
                    <TableCell
                      sx={{
                        ...byMeHeadCellSx,
                        width: '13%',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Shared
                    </TableCell>
                    <TableCell sx={{ ...byMeHeadCellSx, width: '22%' }} align="center">
                      Users
                    </TableCell>
                    <TableCell sx={{ ...byMeHeadCellSx, width: '20%' }} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderFolderRows(null, 0)}
                  {rootDashboardRows}
                  {byMeFolders.length === 0 && rootDashboardsOnly.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ fontFamily: FONT_FAMILY_PRIMARY, color: 'text.secondary' }}>
                        You have not shared any folders or dashboards yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {effectiveTab === 'withMe' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: FONT_FAMILY_PRIMARY, mb: 2 }}>
            Click a row to open the folder or dashboard.
          </Typography>
          {withMeLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              elevation={1}
              sx={{
                borderRadius: '8px',
                overflow: 'auto',
                maxWidth: '100%',
              }}
            >
              <Table size="small" sx={WITH_ME_TABLE_SX}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ ...byMeHeadCellSx, width: '62%', maxWidth: 0 }}>Name</TableCell>
                    <TableCell sx={{ ...byMeHeadCellSx, width: '38%', whiteSpace: 'nowrap' }}>Access</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {withMeItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ fontFamily: FONT_FAMILY_PRIMARY, color: 'text.secondary' }}>
                        Nothing shared with you yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    withMeItems.map((row) => {
                      const kind = row.kind || (row.folderId && !row.dashboardId ? 'folder' : 'dashboard');
                      const key =
                        kind === 'folder' ? `f-${row.folderId}` : `d-${row.dashboardId}`;
                      const name =
                        kind === 'folder' ? row.folderName || 'Folder' : row.dashboardName || 'Dashboard';
                      const access = formatAccessLabel(row.accessLevel);
                      const payload = { ...row, kind };
                      return (
                        <TableRow
                          key={key}
                          hover
                          tabIndex={0}
                          role="button"
                          aria-label={`Open ${kind === 'folder' ? 'folder' : 'dashboard'} ${name}`}
                          onClick={() => handleWithMeRowClick(payload)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleWithMeRowClick(payload);
                            }
                          }}
                          sx={withMeRowSx}
                        >
                          <TableCell
                            sx={{
                              fontFamily: FONT_FAMILY_PRIMARY,
                              width: '62%',
                              maxWidth: 0,
                              verticalAlign: 'middle',
                              borderBottom: 'inherit',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                              {kind === 'folder' ? (
                                <FolderIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                              ) : (
                                <DashboardIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                              )}
                              <Typography variant="body2" noWrap sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
                                {name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: FONT_FAMILY_PRIMARY,
                              width: '38%',
                              verticalAlign: 'middle',
                              whiteSpace: 'nowrap',
                              borderBottom: 'inherit',
                            }}
                          >
                            {access}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <ManageSharedAccessDialog
        open={usersModal.open}
        onClose={() => setUsersModal((s) => ({ ...s, open: false }))}
        onSharesChanged={refreshSharingLists}
        resourceType={usersModal.resourceType}
        resourceId={usersModal.resourceId}
        resourceLabel={usersModal.resourceLabel}
        tenantId={tenantId}
        canManage={
          usersModal.resourceType === 'folder' ? canShareFolder : canShareDash
        }
      />

      <Dialog
        open={confirmRevoke.open}
        onClose={() => !revoking && setConfirmRevoke((s) => ({ ...s, open: false }))}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{
            fontFamily: FONT_FAMILY_PRIMARY,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
          }}
        >
          <Typography component="span" variant="h6" sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
            Remove all access?
          </Typography>
          <IconButton
            aria-label="Close"
            size="small"
            disabled={revoking}
            onClick={() => setConfirmRevoke((s) => ({ ...s, open: false }))}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
            This removes every user you shared{' '}
            <strong>{confirmRevoke.resourceLabel || 'this item'}</strong> with. You can share again later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={revoking} onClick={() => setConfirmRevoke((s) => ({ ...s, open: false }))}>
            Cancel
          </Button>
          <Button color="error" variant="contained" disabled={revoking} onClick={runRevokeAll}>
            {revoking ? 'Removing…' : 'Remove all'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
